/**
 * auth.js - Authentication utility functions
 * 
 * @description
 * This file provides functions for handling authentication tokens and headers.
 */

/**
 * Get the current authentication token from localStorage
 * 
 * @returns {string|null} - The authentication token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Set the authentication token in localStorage
 * 
 * @param {string|null} token - The token to set, or null to remove
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

/**
 * Get the admin authentication token from localStorage
 * 
 * @returns {string|null} - The admin token or null if not found
 */
export const getAdminToken = () => {
  return localStorage.getItem('adminToken');
};

/**
 * Set the admin authentication token in localStorage
 * 
 * @param {string|null} token - The token to set, or null to remove
 */
export const setAdminToken = (token) => {
  if (token) {
    localStorage.setItem('adminToken', token);
  } else {
    localStorage.removeItem('adminToken');
  }
};

/**
 * Check if the user is authenticated
 * 
 * @returns {boolean} - True if the user is authenticated, false otherwise
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Check if the user is an admin
 * 
 * @returns {boolean} - True if the user is an admin, false otherwise
 */
export const isAdmin = () => {
  return !!getAdminToken();
};

/**
 * Get the authorization header for API requests
 * 
 * @param {boolean} adminRoute - Whether this is an admin route
 * @returns {Object} - The headers object with Authorization if a token exists
 */
export const getAuthHeader = (adminRoute = false) => {
  const token = adminRoute ? getAdminToken() : getAuthToken();
  
  return token ? { 
    'Authorization': `Bearer ${token}` 
  } : {};
};

/**
 * Log out the user by removing tokens
 */
export const logout = () => {
  setAuthToken(null);
  setAdminToken(null);
};
